import { deriveLandedCostFromUnitPrice, LandedCostComponents } from '@/helpers/landedCostDerivation';

/**
 * US-092 — cost arithmetic for the Receive Products dialog.
 *
 * One rule underpins all of it: the cost input is the SUPPLIER's clean unit
 * price in the SUPPLIER's (PO's) currency. Anything shown in store currency —
 * the landed-cost hint — is derived from it, never entered directly.
 *
 * Extracted from PurchaseOrderDetail.vue so it is genuinely covered by tests
 * rather than by a replica of the component's logic.
 */

interface ReceiveCostItem {
  id?: number | string | null;
  unitPrice?: number | string | null;
}

/**
 * Pre-fill every receivable line with its ordered unit price.
 *
 * Deliberately uniform across domestic and foreign POs. The previous behaviour
 * substituted `extra.landed_cost` (store currency, shipping already baked in)
 * on foreign POs, which put a landed number into a field the backend now treats
 * as a clean supplier price — double-counting shipping on submit.
 */
export function buildInitialReceiveCosts(
  items: ReceiveCostItem[] | null | undefined,
): { [key: number]: number } {
  const costs: { [key: number]: number } = {};
  if (!Array.isArray(items)) { return costs; }

  items.forEach((item) => {
    if (item?.id == null) { return; }
    costs[Number(item.id)] = Number(item.unitPrice) || 0;
  });

  return costs;
}

/**
 * Supplier → store multiplier for this PO. The rate is locked when the PO is
 * confirmed, so every partial receive against it converts identically.
 */
export function supplierToStoreRate(
  poCurrencyCode: string | null | undefined,
  storeCurrency: string | null | undefined,
  exchangeRate: number | null | undefined,
): number {
  if (!poCurrencyCode || !storeCurrency || poCurrencyCode === storeCurrency) { return 1; }

  const rate = Number(exchangeRate);
  return Number.isFinite(rate) && rate > 0 ? rate : 1;
}

/**
 * True when the landed-cost hint can be trusted for this PO.
 *
 * `supplierToStoreRate` falls back to 1 for a foreign PO with no usable rate,
 * which is the right neutral multiplier but makes the derived figure a
 * SUPPLIER-currency amount labelled as store currency. Showing nothing is
 * better than showing a wrong monetary value next to "this is what Shopify
 * receives".
 */
export function hasUsableSupplierToStoreRate(
  poCurrencyCode: string | null | undefined,
  storeCurrency: string | null | undefined,
  exchangeRate: number | null | undefined,
): boolean {
  if (!poCurrencyCode || !storeCurrency || poCurrencyCode === storeCurrency) { return true; }

  const rate = Number(exchangeRate);
  return Number.isFinite(rate) && rate > 0;
}

/**
 * What Shopify will actually receive for an entered clean cost: converted to
 * store currency, then run through the PO's own shipping/fee allocation.
 */
export function landedCostForEnteredCost(
  enteredCost: number | null | undefined,
  rate: number,
  components: LandedCostComponents | null | undefined,
): number {
  const clean = Number(enteredCost);
  if (!Number.isFinite(clean) || clean <= 0) { return 0; }

  return deriveLandedCostFromUnitPrice(clean * rate, components);
}

/**
 * Whether to surface the supplier cost this receive would replace.
 *
 * Each extra line under the input adds a row of height to every line item, so
 * this one only appears when there is a genuine overwrite to disclose: a cost
 * has been entered, a stored supplier cost exists, and the two actually differ.
 * When they match, nothing changes and the line would be pure noise.
 */
export function shouldShowSupplierCostReplacement(
  enteredCost: number | null | undefined,
  currentSupplierCost: number | null | undefined,
): boolean {
  if (currentSupplierCost == null) { return false; }

  const entered = Number(enteredCost);
  if (!Number.isFinite(entered) || entered <= 0) { return false; }

  // Half-cent tolerance: a stored DECIMAL round-tripping through JSON must not
  // make an unchanged cost look like an overwrite.
  return Math.abs(entered - Number(currentSupplierCost)) >= 0.005;
}
